import {
  Box,
  Cpu,
  File,
  FileArchive,
  FileBox,
  FileIcon,
  FileText,
  ImageIcon,
  Layers,
  PenTool,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { AttachmentType } from '@/redux/attachment/attachment.d';
import { IPostAttachment } from '@/redux/post/post.d';
import {
  CAD_EXTS,
  CAM_PROJECT_EXTS,
  EXTS_BY_KIND,
  MODEL_3D_EXTS,
} from '@/redux/post/post.constants';
import { getAttachmentController } from '@/redux/post/post.slice.helpers';
import { CONTROLLER_CONFIG } from '@/lib/controllerConfig';
import carbidecreate from '@/assets/software/carbidecreate.png';
import carveco from '@/assets/software/carveco.png';
import easel from '@/assets/software/easel.png';
import fusion360 from '@/assets/software/fusion360.png';
import solidworks from '@/assets/software/solidworks.png';
import vectric from '@/assets/software/vectric.png';

const CAD_VECTOR_EXTENSIONS = [
  'svg',
  'ai',
  'dxf',
  'dwg',
  'eps',
  'pdf',
  'ezd',
  ...CAD_EXTS,
].map((ext) => `.${ext}`);

/** True when the file is a CAD/vector file (by extension), not a raster image. */
export const isCadVectorFile = (fileName: string): boolean => {
  const lowerFileName = fileName.toLowerCase();
  return CAD_VECTOR_EXTENSIONS.some((ext) => lowerFileName.endsWith(ext));
};

/**
 * The software a file names by its own extension.
 *
 * Only where the extension IS the vendor's own format. `.svg` and `.dxf` are
 * interchange formats anyone writes, so they keep the generic vector glyph
 * rather than claiming an author they can't know.
 */
const SOFTWARE_BY_EXT: Record<string, string> = {
  crv: vectric,
  crv3d: vectric,
  vcarve: vectric,
  v3m: vectric,
  art: carveco,
  c2d: carbidecreate,
  easel,
  f3d: fusion360,
  sldprt: solidworks,
};

/**
 * What a file actually IS, by extension and nothing else — one table for the
 * whole app, so a `.crv` reads the same in the download modal as in the upload
 * wizard. Before this, each surface decided for itself and most of them had
 * only the resource type to go on, which types nearly every design file as an
 * image.
 */
const DOC_KINDS: Array<{
  Icon: LucideIcon;
  tone: string;
  label: string;
  exts: string[];
}> = [
  // G-code and photos read from EXTS_BY_KIND rather than a list of their own —
  // the wizard's sections and the post carousel already split on it, and a
  // fourth copy is how `.gc` gets forgotten again.
  {
    Icon: Cpu,
    tone: 'text-blue-500',
    label: 'G-code',
    exts: EXTS_BY_KIND.gcode,
  },
  {
    Icon: ImageIcon,
    tone: 'text-teal-500',
    label: 'Image',
    exts: EXTS_BY_KIND.image,
  },
  { Icon: FileText, tone: 'text-rose-500', label: 'PDF', exts: ['pdf'] },
  {
    Icon: PenTool,
    tone: 'text-violet-500',
    label: 'Vector',
    exts: ['dxf', 'dwg', 'svg', 'ai', 'eps'],
  },
  {
    Icon: Layers,
    tone: 'text-sky-500',
    label: 'CAM project',
    exts: CAM_PROJECT_EXTS,
  },
  {
    Icon: Box,
    tone: 'text-emerald-500',
    label: '3D model',
    exts: MODEL_3D_EXTS,
  },
  {
    Icon: FileArchive,
    tone: 'text-amber-500',
    label: 'Archive',
    exts: ['zip', 'rar', '7z'],
  },
];

const PLAIN_DOC = {
  Icon: File,
  tone: 'text-muted-foreground',
  label: 'Document',
};

const BY_EXT = new Map(
  DOC_KINDS.flatMap(({ Icon, tone, label, exts }) =>
    exts.map((ext) => [ext, { Icon, tone, label }] as const)
  )
);

/** The icon and label a file's extension earns — shared with the CAD lists, so
 * a `.crv` reads the same wherever it is shown. */
export function docKindOf(ext: string) {
  return BY_EXT.get(ext) ?? PLAIN_DOC;
}

/** A file's own extension, lowercased and without the dot. */
const extensionOf = (fileName: string): string =>
  fileName.split('.').pop()?.toLowerCase() ?? '';

export type FileMark =
  | { logo: string }
  | { Icon: LucideIcon; tone: string }
  | null;

/**
 * The mark an extension earns: its authoring software where the extension IS
 * that vendor's format, else the glyph for its kind, else nothing — and
 * `null` matters, because it is what lets a caller fall back to whatever else
 * it knows about the file instead of guessing.
 */
export function fileMarkFor(ext: string): FileMark {
  const logo = SOFTWARE_BY_EXT[ext];
  if (logo) return { logo };
  const kind = BY_EXT.get(ext);
  return kind ? { Icon: kind.Icon, tone: kind.tone } : null;
}

/** Renders a resolved mark at the given size. */
export function FileMarkIcon({
  mark,
  className,
}: {
  mark: Exclude<FileMark, null>;
  className: string;
}) {
  if ('logo' in mark) {
    return (
      <img
        src={mark.logo}
        alt=""
        className={cn(className, 'shrink-0 rounded-[3px] object-contain')}
      />
    );
  }
  return (
    <mark.Icon
      className={cn(className, 'shrink-0', mark.tone)}
      strokeWidth={1.5}
    />
  );
}

/**
 * Icon element for an attachment, by extension first.
 *
 * Extension before resourceType because the server types most design files as
 * `image` — which is why a .crv, a .dxf and a .png all arrived with the same
 * picture glyph. resourceType is only the last resort, for a file whose
 * extension the table has never seen.
 */
export const getFileIcon = (attachment: IPostAttachment) => {
  const size = 'h-4 w-4 sm:h-6 sm:w-6';
  const ext = extensionOf(attachment.fileName);

  // Other has no logo, so it falls through to the generic chip.
  if (EXTS_BY_KIND.gcode.includes(ext)) {
    const Icon = CONTROLLER_CONFIG[getAttachmentController(attachment)]?.icon;
    if (Icon) return <Icon className={size} />;
  }

  const mark = fileMarkFor(ext);
  if (mark) return <FileMarkIcon mark={mark} className={size} />;

  switch (attachment.resourceType) {
    case AttachmentType.model:
      return <FileBox className={size} />;
    case AttachmentType.image:
      return <ImageIcon className={size} />;
    default:
      return <FileIcon className={size} />;
  }
};

/** Extract a file extension from a (possibly query-stringed) URL. */
export const getFileExtension = (url: string): string => {
  const matches = url.match(/\.([0-9a-z]+)(?=[?#]|$)/i);
  return matches ? matches[1] : 'download';
};
